import { Flex, Heading, Link, Stack, Text } from "@biblioteksentralen/react";
import { Address, ContactInfo } from "@libry-content/types";
import { sift } from "radash";
import { ReactNode } from "react";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { ContactFormLink } from "../employees/ContactCard";
import { formatPhoneNumber } from "./formatPhoneNumber";

interface Props {
  contactInfo?: ContactInfo;
}

export const formatAddress = (address: Address): string => {
  const postalCodeAndCity = sift([address.postalCode, address.city]).join(" ");
  return sift([address.streetAddress, postalCodeAndCity]).join(", ");
};

function Contactinfo({ contactInfo }: Props) {
  const { t } = useTranslation();
  if (!contactInfo) {
    return null;
  }

  return (
    <Stack direction="column" spacing="2.5rem">
      {contactInfo.visitingAddress && (
        <LibraryInfoSection label={t("Besøk oss")}>
          <Text>{formatAddress(contactInfo.visitingAddress)}</Text>
        </LibraryInfoSection>
      )}
      {contactInfo.postalAddress && (
        <LibraryInfoSection label={t("Postadresse")}>
          <Text>{formatAddress(contactInfo.postalAddress)}</Text>
        </LibraryInfoSection>
      )}
      {(contactInfo.phone || contactInfo.email) && (
        <LibraryInfoSection label={t("Kontakt")} spacing=".5rem">
          {contactInfo.phone && (
            <ContactInfoItem
              title={t("Telefon")}
              value={<Link href={`tel:${contactInfo.phone}`}>{formatPhoneNumber(contactInfo.phone)}</Link>}
            />
          )}
          {contactInfo.email && (
            <ContactInfoItem
              title={t("E-post")}
              value={<Link href={`mailto:${contactInfo.email}`}>{contactInfo.email}</Link>}
            />
          )}
          {contactInfo?.contactFormUrl && (
            <ContactFormLink contactFormUrl={contactInfo.contactFormUrl} label={t("Gå til kontaktskjema")} />
          )}
        </LibraryInfoSection>
      )}
    </Stack>
  );
}

export function LibraryInfoSection(props: { label: string; children: ReactNode; spacing?: string }) {
  return (
    <Stack direction="column" as="section" spacing="1rem">
      <Heading as="h3" size="sm">
        {props.label}
      </Heading>
      <Stack direction="column" spacing={props.spacing ?? 0}>
        {props.children}
      </Stack>
    </Stack>
  );
}

const ContactInfoItem = (props: { title: string; value: ReactNode }) => (
  <Flex direction={{ base: "column", sm: "row" }} gap="0 .75em" alignItems="baseline">
    <Heading as="h4" size="xs" minW="4rem">
      {props.title}
    </Heading>
    {props.value}
  </Flex>
);

export default Contactinfo;
